import { useMemo, useState } from 'react';
import { DataExportPanel } from './data-export-panel.js';
import { AutomationLimitsPanel } from './automation-limits-panel.js';
import { EventStreamPanel } from './event-stream-panel.js';
import { WebhooksPanel } from './webhooks-panel.js';
import { InboundMailboxesPanel } from './inbound-mailboxes-panel.js';
import { JobsPanel } from './jobs-panel.js';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListSetupObjectsResult } from '@whinerp/client';
import {
  CapabilityUnavailable,
  EmptyState,
  ErrorState,
  Icon,
  InlineAction,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { useEntitlement } from '../../app/session.js';
import { defineScreen } from '../types.js';
import { LeadScoringPanel } from './lead-scoring-panel.js';
import { TargetsPanel } from './targets-panel.js';
import { PipelinesPanel } from './pipelines-panel.js';
import { RoutingPanel } from './routing-panel.js';
import { EscalationsPanel } from './escalations-panel.js';
import { DataQualityPanel } from './data-quality-panel.js';
import { SessionsPanel } from './sessions-panel.js';
import { IpAllowlistPanel } from './ip-allowlist-panel.js';
import { EvidencePanel } from './evidence-panel.js';
import { OrgChartPanel } from './org-chart-panel.js';
import { TeamsPanel } from './teams-panel.js';
import { PermissionsPanel } from './permissions-panel.js';
import { SharingPanel } from './sharing-panel.js';
import { CustomObjectsPanel } from './custom-objects-panel.js';
import { FormulaPanel } from './formula-panel.js';
import { CurrencyPanel } from './currency-panel.js';
import { WorkingWeekPanel } from './working-week-panel.js';
import { RecycleBinPanel } from './recycle-bin-panel.js';
import { SsoPanel } from './sso-panel.js';
import { BrandingPanel } from './branding-panel.js';
import { DuplicatesPanel } from './duplicates-panel.js';
import { AuditPanel } from './audit-panel.js';
import { EnvironmentsPanel } from './environments-panel.js';
import { ResidencyPanel } from './residency-panel.js';
import { PrivacyPanel } from './privacy-panel.js';
import { HealthPanel } from './health-panel.js';
import { DeveloperPanel } from './developer-panel.js';
import { BulkPanel } from './bulk-panel.js';
import { EmbedsPanel } from './embeds-panel.js';
import { BookingPagesPanel } from './booking-pages-panel.js';
import { OnboardingTemplatePanel } from '../revenue/onboarding-panel.js';
import { AppsPanel } from './apps-panel.js';
import { TagsPanel } from './tags-panel.js';
import { SupportAccessPanel } from './support-access-panel.js';
import { BackupPanel } from './backup-panel.js';
import { TrackingPanel } from './tracking-panel.js';
import { ProvidersPanel } from './providers-panel.js';
import { ModelRoutingPanel } from './model-routing-panel.js';
import { MiningPanel } from './mining-panel.js';
import { DocumentTemplatesPanel } from './document-templates-panel.js';
import { PartnersPanel } from './partners-panel.js';
import { QuoteTemplatesPanel } from './quote-templates-panel.js';
import { UnitsPanel } from './units-panel.js';
import { TerritoriesPanel } from './territories-panel.js';
import { ValidationRulesPanel } from './validation-rules-panel.js';

/**
 * How many sections Simple holds back.
 *
 * A literal, next to the block that renders them, because the count on screen
 * has to be the number of things the control actually reveals - a figure that
 * drifts from what appears is worse than no figure.
 */
const ADVANCED_SECTIONS = 26;

/**
 * SC-30 Setup home.
 *
 * A card grid rather than a settings tree, and a search across it. Setup is
 * where somebody goes once a quarter, looking for one thing whose name they
 * half remember - so the search matches the card titles and what they
 * contain, and every card says what it is for rather than only what it is
 * called.
 */

interface Category {
  key: string;
  label: string;
  icon: string;
  href: string;
  blurb: string;
  /** Words a person might search for that are not in the title. */
  also: string[];
}

function SetupHomeScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const { session, setPreference } = useSession();
  const mode = session.preferences.density;
  const [search, setSearch] = useState('');

  const objects = useQuery<ListSetupObjectsResult, ApiError>({
    queryKey: ['setup-objects'],
    queryFn: () => api.listSetupObjects({}),
  });

  const people = useQuery({
    queryKey: ['setup-people'],
    queryFn: () => api.listPeople({}),
  });

  const catalogue = useEntitlement('F-046');
  const pricelists = useQuery({
    queryKey: ['pricelists'],
    queryFn: () => api.listPricelists({}),
    // Not asked at all when the module is off: a request that is certain to be
    // refused is a refusal in the network tab and a spinner on the screen.
    enabled: catalogue.state === 'on',
  });

  const categories = useMemo<Category[]>(
    () => [
      {
        key: 'modules',
        label: t('setup.category.modules'),
        icon: 'ToggleLeft',
        href: '/setup/modules',
        blurb: t('setup.modules.body'),
        also: ['features', 'switch', 'plan'],
      },
      {
        key: 'objects',
        label: t('setup.category.objects'),
        icon: 'Table',
        href: '/setup/objects',
        blurb: t('setup.layout.title'),
        also: ['fields', 'layout', 'picklist', 'custom'],
      },
      {
        key: 'data',
        label: t('setup.category.data'),
        icon: 'Upload',
        href: '/setup/import',
        blurb: t('setup.import.title'),
        also: ['import', 'csv', 'spreadsheet', 'rollback'],
      },
      {
        key: 'people',
        label: t('setup.category.people'),
        icon: 'Users',
        href: '#people',
        blurb: t('setup.people.body'),
        also: ['roles', 'teams', 'hierarchy', 'permissions', 'users'],
      },
      {
        key: 'pricing',
        label: t('setup.category.pricing'),
        icon: 'Tag',
        // The first price book, so the card leads somewhere rather than to a
        // page that asks for an id. The panel below is where somebody picks a
        // different one.
        href: '#pricing',
        blurb: t('setup.pricing.body'),
        also: ['price', 'pricelist', 'discount', 'rules'],
      },
      {
        key: 'plan',
        label: t('setup.category.plan'),
        icon: 'CreditCard',
        href: '/setup/plan',
        blurb: t('setup.plan.usage'),
        also: ['billing', 'usage', 'limits', 'upgrade'],
      },
    ],
    [t],
  );

  const term = search.trim().toLowerCase();
  const shown = term
    ? categories.filter((category) =>
        [category.label, category.blurb, ...category.also]
          .join(' ')
          .toLowerCase()
          .includes(term),
      )
    : categories;

  return (
    <div data-component="SetupHome" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{t('setup.title')}</PageHeading>
        <label className="ms-auto flex items-center gap-2">
          <span className="sr-only">{t('setup.search')}</span>
          <input
            data-setup-search
            type="search"
            value={search}
            placeholder={t('setup.search')}
            onChange={(event) => setSearch(event.target.value)}
            className="h-input w-panel-wide max-w-full rounded-control border border-control bg-surface px-3 text-body text-ink placeholder:text-ink-300"
          />
        </label>
      </header>

      {shown.length === 0 ? (
        <SetupHomeEmpty />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((category) => (
            <a key={category.key} href={category.href} data-setup-card={category.key}>
              <Panel>
                <span className="flex items-start gap-2">
                  <Icon name={category.icon} size="inline" decorative />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-body text-ink">{category.label}</span>
                    <span className="text-meta text-ink-meta">{category.blurb}</span>
                  </span>
                </span>
              </Panel>
            </a>
          ))}
        </div>
      )}

      {/* The reporting line and the teams.
          Two concepts kept apart on purpose: a role is a position with people
          beneath it, a team is a group drawn from anywhere. Conflating them is
          how a workspace ends up unable to express a bid team.

          The line is editable here and says what it gives somebody (F-005).
          It was drawn and read-only for a long time, with three operations
          published to change it and no caller for any of them. */}
      <Panel data-region="people" id="people">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('setup.category.people')}</SectionLabel>
          {people.isPending ? (
            <Skeleton className="h-block-md w-full" />
          ) : people.isError ? (
            <ErrorState
              title={t('state.error')}
              body={people.error.message}
              onRetry={() => void people.refetch()}
            />
          ) : (
            <TeamsPanel people={people.data} />
          )}
        </div>
      </Panel>

      {people.isPending || people.isError ? null : <OrgChartPanel people={people.data} />}

      <LeadScoringPanel />

      {/* Targets sit beside the reporting line rather than under Sales,
          because the question they answer - what is this person measured
          against - is the same question the org chart above answers the other
          half of. */}
      <TargetsPanel />

      {/* Your hours and the booking pages that offer them (G-187). Not an
          advanced section: every person keeps their own calendar. */}
      <BookingPagesPanel />

      {/* What every new customer is taken through after the sale (G-199). */}
      <OnboardingTemplatePanel />

      {/* What a deal passes through, and what each step is worth (F-042). The
          columns have been per-pipeline since the first sales migration and
          nothing could set any of them. */}
      <PipelinesPanel />

      {/* Who a new lead goes to (F-034). Beside the teams it routes into,
          because a rule that points at an empty team assigns nobody and the
          fix is one panel up. */}
      {people.isPending || people.isError ? null : (
        <RoutingPanel people={people.data.people} teams={people.data.teams} />
      )}

      {/* When work goes undone for too long, who hears (G-225). */}
      {people.isPending || people.isError ? null : <EscalationsPanel people={people.data.people} />}

      {/* How complete, duplicated and stale the records are (G-275). */}
      <DataQualityPanel />

      {/* Who is signed in, and signing someone out everywhere (G-281). */}
      <SessionsPanel />
      {/* The networks it may be used from (G-282). */}
      <IpAllowlistPanel />
      {/* What a security questionnaire asks, answered from the live settings (G-279). */}
      <EvidencePanel />

      {/* What people may do, which is neither their position nor their team.
          Kept next to the reporting line because that is where an
          administrator goes looking for it, and separate because a role is not
          a permission - conflating them is how "manager" comes to mean twelve
          different things in one workspace. */}
      {people.isPending || people.isError || objects.isPending || objects.isError ? null : (
        <PermissionsPanel people={people.data.people} objects={objects.data.objects} />
      )}

      {/*
        Who from support may come in, and who has been (F-144).

        Not held back with the specialist half. A workspace should not have to
        switch to Advanced to find out that support can let itself in - and it
        cannot, which is the thing this panel exists to say.
      */}
      <SupportAccessPanel />

      {/* Whether this workspace has a backup, said plainly (F-145). Not held
          back with the specialist half either: an unclear backup state causes
          more anxiety than no backup page at all. */}
      <BackupPanel />

      {/* What is resolved from a visitor address, and how long the address is
          kept (F-152). */}
      <TrackingPanel />

      {/* What this deployment can actually reach, and in which mode (part 3). */}
      <ProvidersPanel />
      {/* Beside the provider list: which providers exist, then which one
          answers and whose account pays (F-114). */}
      <ModelRoutingPanel />

      {/*
        The specialist half, held back in Simple (F-014).

        Somebody setting a workspace up in their first week meets "invite
        people" and "turn modules on". They should not meet sharing rules,
        formula fields, custom objects and single sign-on in the same scroll.

        Held back, not removed: the count is on screen and one control reveals
        them. A Setup page that silently lacks the section an article told
        somebody to look for is worse than a long one.
      */}
      {mode === 'advanced' ? (
        <>
          <div data-advanced-section className="contents">
            {/* Who can see each kind of record, which is a different question from
                what a person may do - and the one an administrator asks when a rep
                says they cannot find a deal that plainly exists. */}
            {people.isPending || people.isError ? null : (
              <SharingPanel teams={people.data.teams} roles={people.data.roles} />
            )}
          </div>

          <div data-advanced-section className="contents">
            {/* Objects this workspace invented, and the wizard that makes one. */}
            <CustomObjectsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Fields whose value is worked out rather than typed. */}
            {objects.isPending || objects.isError ? null : <FormulaPanel objects={objects.data.objects} />}
          </div>

          <div data-advanced-section className="contents">
            {/* What a record may not be saved as, per object, named to the
                person it stops (F-091). Beside formulas, because both are
                what an administrator adds to an object after its fields. */}
            {objects.isPending || objects.isError || people.isPending || people.isError ? null : (
              <ValidationRulesPanel objects={objects.data.objects} roles={people.data.roles} />
            )}
          </div>

          <div data-advanced-section className="contents">
            {/* What a currency was worth, and from when. */}
            <CurrencyPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* The days and hours the office works, which is what an SLA clock
                counts when a policy says it stops outside them. It was a
                constant - Sunday to Thursday - so a European workspace's
                deadlines ran through Sunday and stopped on Friday. */}
            <WorkingWeekPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* How people get in. Next to the people panels because "who can sign
                in" is the same question as "who is here", asked from the other end.
                Roles come from the same query the reporting line uses: an account
                created at sign-in needs one, and a list of two would be two
                answers. */}
            {people.isPending || people.isError ? null : <SsoPanel roles={people.data.roles} />}
          </div>

          <div data-advanced-section className="contents">
            {/* What the workspace looks like to its own people and its customers. */}
            <BrandingPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Pairs that look like the same person, and the merge preview. */}
            <DuplicatesPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* What has been deleted and not yet lost, with the window each row
                has left. Delete and restore were built and the bin was not, so
                it only ever grew. */}
            <RecycleBinPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Who changed what, across the whole workspace. */}
            <AuditPanel />
            <JobsPanel />
            <AutomationLimitsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Moving configuration between a sandbox and production. */}
            <EnvironmentsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Where this workspace's data is held, and that it cannot move. */}
            <ResidencyPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* People asking for their data back, or asking to be removed. */}
            <PrivacyPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* What is failing now, with the number each judgement was made on. */}
            <HealthPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Tokens a program calls with, and the contract it calls against. */}
            <DeveloperPanel />
            <WebhooksPanel />
            <InboundMailboxesPanel />
            <EventStreamPanel />
            <DataExportPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* High-volume work, and how far it has got. */}
            <BulkPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Widgets other sites render, and what they would actually show. */}
            <EmbedsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Third-party code, what it was granted, and what it has been doing. */}
            <AppsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* The tag vocabulary, and the near-duplicates creeping into it. */}
            <TagsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Companies nobody here knows yet, and what importing them costs
                before the press (F-154). */}
            <MiningPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Templates a quote or a deal is printed from (F-050). */}
            <DocumentTemplatesPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Partners, what they registered, and the collisions they are
                never told about (F-061). */}
            <PartnersPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Quote skeletons a rep starts from, versioned (F-158). */}
            <QuoteTemplatesPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Units a product is counted and sold in (F-161). */}
            <UnitsPanel />
          </div>

          <div data-advanced-section className="contents">
            {/* Who works which companies, and moving them with history (F-054). */}
            <TerritoriesPanel />
          </div>
        </>
      ) : (
        <Panel data-region="advanced-held-back">
          <div className="flex flex-col gap-2">
            <p className="text-meta text-ink-meta">{t('setup.advancedHidden')}</p>
            <InlineAction
              data-setup-show-advanced
              onClick={() => setPreference({ density: 'advanced' })}
            >
              {t('setup.showAdvanced', {
                values: { count: formatCount(ADVANCED_SECTIONS, format) },
              })}
            </InlineAction>
          </div>
        </Panel>
      )}

      {/* The price books.
          They existed, and nothing listed them: the rule editor could open one
          whose id you already had, and nothing produced an id. A workspace with
          three had two of them reachable only by typing a uuid into the
          address bar. */}
      <Panel data-region="pricing" id="pricing">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.category.pricing')}</SectionLabel>
          {/* Switched off is not broken.
              This rendered "Something went wrong" over a 402, which tells an
              administrator their software is faulty when what is true is that
              a module is off and they are the person who can turn it on. */}
          {catalogue.state === 'off' || catalogue.state === 'plan-locked' ? (
            <CapabilityUnavailable
              kind={catalogue.state === 'off' ? 'module-off' : 'plan-locked'}
              capability={t('setup.category.pricing')}
              title={t('capability.moduleOff.title')}
              body={t('capability.moduleOff.body', {
                values: { capability: t('setup.category.pricing') },
              })}
              action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
            />
          ) : pricelists.isPending ? (
            <Skeleton className="h-block-sm w-full" />
          ) : pricelists.isError ? (
            <ErrorState
              title={t('state.error')}
              body={pricelists.error.message}
              onRetry={() => void pricelists.refetch()}
            />
          ) : pricelists.data.pricelists.length === 0 ? (
            <p className="py-1 text-body text-ink-meta">{t('pricelist.none')}</p>
          ) : (
            <ul className="flex flex-col">
              {pricelists.data.pricelists.map((list) => (
                <li
                  key={list.pricelistId}
                  data-pricelist={list.pricelistId}
                  className="flex items-center gap-3 border-t border-t-rule py-1 first:border-t-0"
                >
                  <Icon name="Tag" size="inline" decorative />
                  <a
                    href={`/pricelists/${list.pricelistId}`}
                    className="min-w-0 flex-1 truncate text-body text-ink hover:text-bronze-text"
                  >
                    {list.name}
                  </a>
                  <span className="shrink-0 text-meta text-ink-meta">{list.currency}</span>
                  {/* Said in words, not by greying the row out. A price book
                      that decides nothing looks exactly like one that does,
                      and the difference is what somebody came here to find. */}
                  {!list.active ? (
                    <span data-pricelist-state="inactive" className="shrink-0 rounded-chip bg-sunken px-1.5 text-meta text-ink-600">
                      {t('pricelist.inactive')}
                    </span>
                  ) : list.expired ? (
                    <span data-pricelist-state="expired" className="shrink-0 rounded-chip bg-warning-tint px-1.5 text-meta text-warning-text">
                      {t('pricelist.expired')}
                    </span>
                  ) : null}
                  <Num className="shrink-0 text-meta text-ink-meta">
                    {list.rules === 0
                      ? t('pricelist.empty')
                      : t('pricelist.ruleCount', { values: { count: formatCount(list.rules, format) } })}
                  </Num>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      {/* The objects, each with how many custom fields it has - the number an
          administrator is usually looking for when they open this page. */}
      <Panel data-region="objects">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.category.objects')}</SectionLabel>
          {objects.isPending ? (
            <Skeleton className="h-block-sm w-full" />
          ) : objects.isError ? (
            <ErrorState
              title={t('state.error')}
              body={objects.error.message}
              onRetry={() => void objects.refetch()}
            />
          ) : (
            <ul className="flex flex-col">
              {objects.data.objects.map((object) => (
                <li
                  key={object.key}
                  data-setup-object={object.key}
                  className="flex items-center gap-3 border-t border-t-rule py-1 first:border-t-0"
                >
                  <Icon name={object.icon} size="inline" decorative />
                  <a
                    href={`/setup/objects/${object.key}/layout`}
                    className="flex-1 truncate text-body text-ink hover:text-bronze-text"
                  >
                    {object.pluralLabel}
                  </a>
                  <Num className="text-meta text-ink-meta">
                    {formatCount(object.customFields, format)}
                  </Num>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  );
}

function SetupHomeEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="filtered-to-nothing"
      illustration="MagnifyingGlassMinus"
      title={t('state.filteredToNothing')}
      body={t('state.filteredToNothing.body')}
      primary={{ label: t('empty.action.openSetup'), icon: 'Gear', href: '/setup' }}
    />
  );
}

function SetupHomeLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-md" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-block-xs w-full" />
        ))}
      </div>
    </div>
  );
}

function SetupHomeError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Setup could not be loaded"
      body={error instanceof Error ? error.message : 'Setup did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-30',
  route: '/setup',
  surface: 'page',
  title: 'Setup',
  // F-046: the price books are listed here and reached from here. The product
  // catalogue itself is read rather than managed - the acceptance criteria say
  // it is shared with the wider WhinERP inventory module, which is why no
  // screen in 04_Screens edits a product.
  features: ['F-013', 'F-046', 'F-005', 'F-006', 'F-007', 'F-008', 'F-010', 'F-011', 'F-014', 'F-015', 'F-017', 'F-018', 'F-023', 'F-025', 'F-146', 'F-147', 'F-148', 'F-124', 'F-125', 'F-126', 'F-128', 'F-135', 'F-136', 'F-137', 'F-033', 'F-076', 'F-053', 'F-042', 'F-036', 'F-034', 'F-056', 'F-144', 'F-145', 'F-152', 'F-154', 'F-050', 'F-091'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'nav', item: 'setup' }],
  components: [],
  elements: ['SetupHome'],
  states: {
    empty: SetupHomeEmpty,
    loading: SetupHomeLoading,
    error: SetupHomeError,
  },
  component: SetupHomeScreen,
});

export default screen;
